import { Arg, Ctx, Directive, Mutation, Resolver } from 'type-graphql'
import { In, Repository } from 'typeorm'

import { Domain, getRepository } from '@things-factory/shell'

import { Setting } from '../setting/setting'
import { PartnerSetting } from './partner-setting'
import { PartnerSettingPatch } from './partner-setting-type'

@Resolver(PartnerSetting)
export class PartnerSettingMutation {
  @Directive('@transaction')
  @Mutation(returns => [PartnerSetting], { description: "To modify multiple PartnerSettings' information" })
  async updateMultiplePartnerSetting(
    @Arg('patches', type => [PartnerSettingPatch]) patches: PartnerSettingPatch[],
    @Ctx() context: ResolverContext
  ): Promise<PartnerSetting[]> {
    const { tx, domain, user } = context.state
    const partnerSettingRepo: Repository<PartnerSetting> =
      tx?.getRepository(PartnerSetting) || getRepository(PartnerSetting)
    const domainRepo: Repository<Domain> = tx?.getRepository(Domain) || getRepository(Domain)
    const settingRepo: Repository<Setting> = tx?.getRepository(Setting) || getRepository(Setting)

    const { createRecords, updateRecords } = patches.reduce(
      (records, patch) => {
        if (patch.cuFlag === '+') records.createRecords.push(patch)
        if (patch.cuFlag.toUpperCase() === 'M') records.updateRecords.push(patch)

        return records
      },
      { createRecords: [], updateRecords: [] }
    )

    let results: PartnerSetting[] = []

    if (createRecords?.length) {
      await Promise.all(
        createRecords.map(async (record: PartnerSetting) => {
          const partnerDomain: Domain = await domainRepo.findOneBy({ id: record.partnerDomain.id })
          const setting: Setting = await settingRepo.findOneBy({ id: record.setting.id })

          const result: PartnerSetting = await partnerSettingRepo.save({
            domain,
            creator: user,
            updater: user,
            ...record,
            name: setting.name,
            description: setting.description,
            category: setting.category,
            partnerDomain,
            setting
          })

          results.push(result)
        })
      )
    }

    if (updateRecords?.length) {
      await Promise.all(
        updateRecords.map(async (record: PartnerSetting) => {
          const partnerDomain: Domain = await domainRepo.findOneBy({ id: record.partnerDomain.id })
          const setting: Setting = await settingRepo.findOneBy({ id: record.setting.id })

          const partnerSetting: PartnerSetting = await partnerSettingRepo.findOneBy({ id: setting.id })
          if (!record.id) throw new Error('Cannot update record without its ID')

          const result = await partnerSettingRepo.save({
            ...partnerSetting,
            ...record,
            name: setting.name,
            description: setting.description,
            category: setting.category,
            updater: user,
            partnerDomain,
            setting
          })

          results.push(result)
        })
      )
    }

    return results
  }

  @Directive('@transaction')
  @Mutation(returns => Boolean, { description: 'To delete multiple partnerSettings' })
  async deletePartnerSettings(
    @Arg('ids', type => [String]) ids: string[],
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { domain, tx } = context.state

    await tx.getRepository(PartnerSetting).delete({ id: In(ids), domain: { id: domain.id } })
    return true
  }
}
